<script setup lang="ts">
import { ref, useTemplateRef, onUnmounted, onMounted } from "vue"
import Card from "primevue/card"
import Divider from "primevue/divider"
import AlertsTable from "@/components/alerts/AlertsTable.vue"
import AlertsFilters from "@/components/alerts/AlertsFilters.vue"

import { useFundingRateSignalsStore } from "@/stores/fundingRateSignalsStore"
import { getFundingRateSignalsTableColumns } from "@/columns/fundingRateSignalsColumns"
import capitalize from "lodash/capitalize"
import { convertPeriodStr, keysToSnake, parseCommaSeparatedAssets } from "@/utils"
import { extractFormData, extractZodFromForm } from "@/utils/forms"
import { z } from "zod"
import { createFundingRateSignalsFilters } from "@/filters/fundingRateSignalsFilters"
import type { FormSubmitEvent } from "@primevue/forms"
import { UPDATE_DATA_DELAY } from "@/utils/consts"

let updateRulesTimer = 0
let updateDataTimer = 0

const alertsTable = useTemplateRef('alertsTable')
const tableColumns = getFundingRateSignalsTableColumns()
const formattingRules = ref({})
const pageInited = ref(false)

const fundingSignalsStore = useFundingRateSignalsStore()
const filters = createFundingRateSignalsFilters()

// Данные формы и zod правила
const formData = ref(extractFormData(filters))
const zodObjectData = extractZodFromForm(filters)
const zodForm = z.object(zodObjectData)

const updateRules = () => {
  formattingRules.value = {
    'symbol_name': (v: string | null) => {
      if (!v) return '—'
      return v.split('/')[0]
    },
    'spread': (v: string | null) => {
      if (!v) return '—'
      return Number(v).toFixed(2) + '%'
    },
    'price_spread': (v: string | null) => {
      if (!v) return '—'
      return Number(v).toFixed(2) + '%'
    },
    'long.exchange_name': (v: string | null) => {
      if (!v) return '—'
      return capitalize(v)
    },
    'long.funding_rate': (v: string | null) => {
      if (!v) return '—'
      return Number(v).toFixed(4)
    },
    'short.exchange_name': (v: string | null) => {
      if (!v) return '—'
      return capitalize(v)
    },
    'short.funding_rate': (v: string | null) => {
      if (!v) return '—'
      return Number(v).toFixed(4)
    },
    period: convertPeriodStr
  }
}

const refresher = () => {
  alertsTable.value?.refresh()
  updateDataTimer = window.setTimeout(refresher, UPDATE_DATA_DELAY)
}

onMounted(async () => {
  fundingSignalsStore.clear()
  updateRules()
  updateRulesTimer = window.setInterval(updateRules, 1000)
  await alertsTable.value?.onPage(null, 'spread', -1)
  pageInited.value = true
  // Обновляем контент каждые X секунд
  updateDataTimer = window.setTimeout(refresher, UPDATE_DATA_DELAY)
})

onUnmounted(() => {
  if (updateRulesTimer) window.clearInterval(updateRulesTimer)
  if (updateDataTimer) window.clearTimeout(updateDataTimer)
})

// Обработка формы фильтрации
const onFilterFormSubmit = async (e: FormSubmitEvent) => {
  if (!e.valid || !pageInited.value) {
    return
  }
  const values = keysToSnake(e.values)
  if (typeof values.symbols === 'string') {
    values.symbols = parseCommaSeparatedAssets(values.symbols)
  }
  fundingSignalsStore.filters = values
  await alertsTable.value?.onPage(null)
}

</script>

<template>
  <Card class="md:mb-2" pt:body:class="p-0" pt:root:class="rounded-none md:rounded-xl">
    <template #content>
      <AlertsFilters :inited="pageInited" panel-class="px-2 border-none mb-0" :loading="fundingSignalsStore.loading"
        :form-submit="onFilterFormSubmit" :init-values="formData" :filters="filters" :zod="zodForm"
        grid-classes="gap-y-3 gap-x-3 grid-cols-1 md:grid-cols-4">
        <template #top-left>
          <span class="pit success font-bold">{{ $t('fundingSignalsArb') }}</span>
        </template>
      </AlertsFilters>
      <Divider class="my-0"></Divider>
      <AlertsTable :paginator="true" :lazy="true" :formatting-rules="formattingRules" ref="alertsTable"
        :columns="tableColumns" :fake-rows-count="30" :store="fundingSignalsStore">
      </AlertsTable>
    </template>
  </Card>
</template>
